import React, {useCallback, useContext, useEffect} from 'react';

import {DISCARD_CHANGES_SUCCESS_MESSAGE, SAVE_CHANGES_SUCCESS_MESSAGE} from 'src/constants';
import {useConfirmationChunk} from 'src/contexts/confirmation-context';
import {MessageContext} from 'src/contexts/message-context';
import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';
import {ConnectionStatus} from 'src/modules/profile/account/tabs/license-and-features/crm-tab/components/connection-status';
import {CrmType as CrmTypes} from 'src/openapi/admin/models/CrmType';

import {FlatButton} from '@pb/kit-component-button';
import {Group, Item} from '@pb/kit-component-group';
import {Icon, Sizes as IconSizes, Icons} from '@pb/kit-component-icon';
import {BasicIconButton} from '@pb/kit-component-icon-button';
import {SlideBoxButton} from '@pb/kit-component-slide-box';


import {CrmRow} from '../../components/crm-row';
import {useCrmLimits} from '../../useCrmLimits';
import {useEnabledCrmModules} from '../../useEnabledCrmModules';
import {useLinkageStatistic} from '../../useLinkageStatistic';
import {useSyncLinkageStatistic} from '../../useSyncLinkageStatistic';

import {HubspotSection} from './hubspot-section';
import {useGetHubspotAuth} from './useGetHubspotAuth';
import {useHubspotRevokeToken} from './useHubspotRevokeToken';
import {useHubspotSettings} from './useHubspotSettings';
import {useHubspotTestConnection} from './useHubspotTestConnection';
import {useHubspotUnlinkConnection} from './useHubspotUnlinkConnection';


import type {CrmComponentProps} from '../../license-crm-tab';

export const HubspotRow = (props: CrmComponentProps) => {
    const {
        accountId,
        licenseId,
        currentCrm,
        runSaveChanges,
        runDiscardChanges,

        onChangeActiveCrm,
        onChangesSaved,
        onChangesDiscarded,
        onActionDisabled,
        editDisabled,
    } = props;

    const {setErrorMessage, setSuccessMessage} = useContext(MessageContext);

    const {isSomeChunkChanged, isCurrentChunkChanged, setIsChunkChanged, onShowModal, proceed} = useConfirmationChunk({
        chunkName: 'hubspot-crm',
        onProceed: () => {
            onChangesSaved();
            setSuccessMessage(SAVE_CHANGES_SUCCESS_MESSAGE);
        },
    });

    const {
        isLoading: isHubspotModuleLoading,
        loadingError: hubspotModuleLoadingError,
        enabled,
        saveCrmModuleState,
    } = useEnabledCrmModules({licenseId, crmType: CrmTypes.HUBSPOTCRM});

    const {
        isLoading: isHubspotLimitsLoading,
        loadingError: hubspotLimitsLoadingError,
        isChanged: isCrmLimitsChanged,

        data: crmLimits,
        setData: setCrmLimits,

        saveChanges: saveCrmLimits,
        discardChanges: discardCrmLimits,
    } = useCrmLimits({licenseId, crmType: CrmTypes.HUBSPOTCRM, accountId});

    const {
        isLoading: isHubspotSettingsLoading,
        loadingError: hubspotSettingsLoadingError,
        isChanged: isHubspotSettingsChanged,

        data: hubspotSettings,
        setData: setHubspotSettings,

        saveChanges: saveHubspotSettings,
        discardChanges: discardHubspotSettings,
        loadData: loadHubspotSettings,
    } = useHubspotSettings({accountId});

    const {
        isLoading: isLinkageStatisticLoading,
        loadingError: linkageStatisticLoadingError,
        data: linkageStatistic,
        loadData: loadLinkageStatistic,
    } = useLinkageStatistic({accountId, licenseId, crmType: CrmTypes.HUBSPOTCRM});

    const {
        busy: isSyncLinkageStatisticLoading,
        event: syncHubspotLinkageStatistic,
    } = useSyncLinkageStatistic({accountId, crmType: CrmTypes.HUBSPOTCRM});

    const {
        isLoading: isHubspotConnectionLoading,
        loadingError: hubspotConnectionLoadingError,
        data: connectionStatus,
        loadData: testConnection,
    } = useHubspotTestConnection({accountId});

    const {
        busy: unlinkBusy,
        event: unlinkHubspotConnection,
    } = useHubspotUnlinkConnection({accountId});

    const {
        busy: revokeTokenBusy,
        event: revokeHubspotOauthToken,
    } = useHubspotRevokeToken({accountId});

    const {
        isOAuthRedirectUrlLoading,
        oAuthRedirectUrlLoadingError,
        redirectToHubspotOAuth,
    } = useGetHubspotAuth({
        accountId,
        onConnectionSuccessful: () => testConnection(),
    });

    const saveChanges = useCallback(async () => {
        const calls = [];

        if (isCrmLimitsChanged) {
            calls.push(await saveCrmLimits());
        }

        if (isHubspotSettingsChanged) {
            calls.push(await saveHubspotSettings());
        }

        const errors = calls.filter(Boolean);

        if (errors.length) {
            onChangesSaved();
            setErrorMessage(errors.join(' '));
        } else {
            proceed();
        }
    }, [
        isCrmLimitsChanged,
        saveCrmLimits,
        isHubspotSettingsChanged,
        saveHubspotSettings,
        onChangesSaved,
        proceed,
        setErrorMessage,
    ]);

    const discardChanges = useCallback(() => {
        discardCrmLimits();
        discardHubspotSettings();

        onChangesDiscarded();
        setSuccessMessage(DISCARD_CHANGES_SUCCESS_MESSAGE);
    }, [discardCrmLimits, discardHubspotSettings, onChangesDiscarded, setSuccessMessage]);

    const changeActiveCrm = useCallback(() => {
        if (isCurrentChunkChanged) {
            onShowModal(() => {
                discardChanges();
                onChangeActiveCrm(currentCrm !== CrmTypes.HUBSPOTCRM ? CrmTypes.HUBSPOTCRM : null);
            });
        } else {
            onChangeActiveCrm(currentCrm !== CrmTypes.HUBSPOTCRM ? CrmTypes.HUBSPOTCRM : null);
        }
    }, [isCurrentChunkChanged, discardChanges, currentCrm, onShowModal, onChangeActiveCrm]);

    const toggleCrmModules = useCallback<React.MouseEventHandler<HTMLDivElement>>(event => {
        if (enabled && isCurrentChunkChanged) {
            onShowModal(() => {
                discardChanges();
                onChangeActiveCrm(null);
                saveCrmModuleState(false);
            });
        } else if (enabled) {
            onChangeActiveCrm(null);
            saveCrmModuleState(false);
        } else {
            saveCrmModuleState(true);
        }
        event.preventDefault();
    }, [isCurrentChunkChanged, discardChanges, enabled, onChangeActiveCrm, saveCrmModuleState, onShowModal]);

    const syncLinkageStatistic = useCallback(async () => {
        const error = await syncHubspotLinkageStatistic();

        if (error) {
            setErrorMessage(error);
        } else {
            setSuccessMessage('Reconciliation completed');
            if (loadLinkageStatistic) {
                loadLinkageStatistic();
            }
        }
    }, [syncHubspotLinkageStatistic, loadLinkageStatistic, setErrorMessage, setSuccessMessage]);

    const revokeOauthToken = useCallback(async () => {
        const error = await revokeHubspotOauthToken();

        if (error) {
            setErrorMessage(error);
        } else {
            testConnection();
        }
    }, [revokeHubspotOauthToken, testConnection, setErrorMessage]);

    const unlinkConnection = useCallback(async () => {
        const error = await unlinkHubspotConnection();

        if (error) {
            setErrorMessage(error);
        } else {
            testConnection();
        }
    }, [unlinkHubspotConnection, testConnection, setErrorMessage]);

    useEffectWithDeps((prev, current) => {
        if (currentCrm === CrmTypes.HUBSPOTCRM && prev.currentCrm !== current.currentCrm) {
            if (loadLinkageStatistic) {
                loadLinkageStatistic();
            }

            if (loadHubspotSettings) {
                loadHubspotSettings();
            }
        }
    }, {
        currentCrm,
        loadHubspotSettings,
        loadLinkageStatistic,
    });

    useEffect(() => {
        if (licenseId) {
            if (loadLinkageStatistic) {
                loadLinkageStatistic();
            }
            if (loadHubspotSettings) {
                loadHubspotSettings();
            }
        }
    }, [licenseId, loadLinkageStatistic, loadHubspotSettings]);

    useEffect(() => {
        setIsChunkChanged([
            isCrmLimitsChanged,
            isHubspotSettingsChanged,
        ].some(Boolean));
    }, [
        setIsChunkChanged,
        isCrmLimitsChanged,
        isHubspotSettingsChanged,
    ]);

    useEffect(() => {
        if (runSaveChanges && isCurrentChunkChanged) {
            saveChanges();
        }
    }, [runSaveChanges, saveChanges, isCurrentChunkChanged]);

    useEffect(() => {
        if (runDiscardChanges && isCurrentChunkChanged) {
            discardChanges();
        }
    }, [runDiscardChanges, discardChanges, isCurrentChunkChanged]);

    const isRowLoading = [
        isHubspotModuleLoading,
        isHubspotLimitsLoading,
    ].some(Boolean);

    const isLoading = isHubspotSettingsLoading;

    const loadingError = [
        hubspotModuleLoadingError,
        hubspotLimitsLoadingError,
        hubspotSettingsLoadingError,
        oAuthRedirectUrlLoadingError,
        hubspotConnectionLoadingError,
    ].find(Boolean) || null;

    useEffect(() => {
        onActionDisabled(!isCurrentChunkChanged || isRowLoading || isLoading || !!loadingError);
    }, [isCurrentChunkChanged, isLoading, isRowLoading, loadingError, onActionDisabled]);

    return (
        <CrmRow
            isLoading={isRowLoading}
            currentCrm={currentCrm}
            loadingError={loadingError}
            editDisabled={editDisabled}
            crmType={CrmTypes.HUBSPOTCRM}
            crmName="HubSpot"
            enabled={enabled}
            onToggleCrmModules={toggleCrmModules}
            crmLimits={crmLimits}
            connectionStatus={(
                <ConnectionStatus {...{
                    enabled,
                    isTestReady: true,
                    connectionStatus,
                    unlinkBusy,
                    unlinkDisabled: editDisabled,
                    onUnlink: unlinkConnection,
                }}
                />
            )}
            slideBoxButton={(
                <SlideBoxButton onClick={changeActiveCrm}>
                    {() => (
                        <Item className="pdy-xs">
                            {enabled ? (
                                <Group alignItems="center">
                                    <FlatButton
                                        startIcon={<Icon type={Icons.REPEAT}/>}
                                        busy={isHubspotConnectionLoading}
                                        onClick={() => testConnection()}>
                                        Test connection
                                    </FlatButton>
                                    <Item className="pr-xs">
                                        <BasicIconButton
                                            iconProps={{
                                                type: currentCrm === CrmTypes.HUBSPOTCRM
                                                    ? Icons.ANGLE_UP
                                                    : Icons.ANGLE_DOWN,
                                                size: IconSizes.Size_12,
                                            }}
                                            disabled={
                                                isSomeChunkChanged(chunk => chunk.includes('crm')) &&
                                                currentCrm !== CrmTypes.HUBSPOTCRM
                                            }
                                            onClick={changeActiveCrm}
                                        />
                                    </Item>
                                </Group>
                            ) : (
                                <FlatButton
                                    disabled={
                                        isSomeChunkChanged(chunk => chunk.includes('crm')) &&
                                        currentCrm !== CrmTypes.HUBSPOTCRM
                                    }
                                    startIcon={<Icon type={Icons.PENCIL}/>}
                                    onClick={changeActiveCrm}
                                    endIcon={(
                                        <Icon
                                            type={currentCrm === CrmTypes.HUBSPOTCRM
                                                ? Icons.ANGLE_UP
                                                : Icons.ANGLE_DOWN}
                                        />
                                    )}>
                                    Configure settings
                                </FlatButton>
                            )}
                        </Item>
                    )}
                </SlideBoxButton>
            )}>
            <HubspotSection
                readonly={editDisabled}
                disabled={!enabled}
                isLoading={isHubspotSettingsLoading}
                isLinkageStatisticLoading={isLinkageStatisticLoading}
                isSyncLinkageLoading={isSyncLinkageStatisticLoading}
                isAuthRedirectUrlLoading={isOAuthRedirectUrlLoading}
                revokeTokenBusy={revokeTokenBusy}

                linkageStatisticLoadingError={linkageStatisticLoadingError}
                limits={crmLimits}
                settings={hubspotSettings}
                linkageStatistic={linkageStatistic}
                orgId={connectionStatus.status.crmUserInfo?.orgId}

                onChangeLimits={setCrmLimits}
                onChangeSettings={setHubspotSettings}
                onSyncLinkageStatistic={syncLinkageStatistic}
                onSetupOAuthLogin={redirectToHubspotOAuth}
                onRevokeOAuthToken={revokeOauthToken}
            />
        </CrmRow>
    );
};
